import type {
  ExtractKeywordsResult,
  NormalizeSkillsResult,
  ScoreSkillOverlapResult,
  TaxonomyLocale,
} from '@tabbio/types';
import { api } from './api';
import {
  searchCanonicalRoles,
  searchCanonicalSkills,
  type CanonicalTaxonomyOption,
} from './job-taxonomy';

function appendMaybeParam(
  params: URLSearchParams,
  key: string,
  value?: string | number | null,
) {
  if (value === undefined || value === null || value === '') {
    return;
  }

  params.set(key, String(value));
}

async function tryFetchTaxonomyOptions(
  path: string,
  fallback: () => CanonicalTaxonomyOption[],
  query?: string,
  limit = 24,
  kind: 'skill' | 'role' = 'skill',
) {
  const params = new URLSearchParams();
  appendMaybeParam(params, 'query', query?.trim() || null);
  appendMaybeParam(params, 'limit', limit);
  appendMaybeParam(params, 'mode', query?.trim() ? 'search' : 'select');

  try {
    const res = await api.get<any[]>(
      `${path}${params.size ? `?${params.toString()}` : ''}`,
    );

    if (res.error || !Array.isArray(res.data)) {
      return fallback();
    }

    return res.data.map((item) => ({
      id: item.id,
      key: item.key,
      familyKey: item.familyKey,
      familyLabel: item.familyName,
      familyLabelAr: item.familyNameAr ?? item.familyName,
      label: item.name,
      labelAr: item.nameAr ?? item.name,
      aliases: Array.isArray(item.aliases) ? item.aliases : [],
      kind,
    })) satisfies CanonicalTaxonomyOption[];
  } catch {
    return fallback();
  }
}

export async function fetchCatalogSkills(
  query = '',
  limit = 24,
): Promise<CanonicalTaxonomyOption[]> {
  return tryFetchTaxonomyOptions(
    '/api/taxonomy/skills',
    () => searchCanonicalSkills(query, limit),
    query,
    limit,
    'skill',
  );
}

export async function fetchCatalogRoles(
  query = '',
  limit = 24,
): Promise<CanonicalTaxonomyOption[]> {
  return tryFetchTaxonomyOptions(
    '/api/taxonomy/roles',
    () => searchCanonicalRoles(query, limit),
    query,
    limit,
    'role',
  );
}

export async function normalizeSkillsApi(
  skills: string[],
  locale?: TaxonomyLocale,
): Promise<NormalizeSkillsResult> {
  const cleaned = skills
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  if (cleaned.length === 0) {
    return { results: [] };
  }

  const res = await api.post<NormalizeSkillsResult>('/api/taxonomy/normalize-skills', {
    skills: cleaned,
    locale,
  });
  if (res.error || !res.data) {
    return { results: [] };
  }
  return res.data;
}

export async function extractKeywordsApi(
  text: string,
  locale?: TaxonomyLocale,
  limit?: number,
): Promise<ExtractKeywordsResult> {
  if (!text.trim()) {
    return { skills: [], roles: [], unmatched: [] };
  }

  const res = await api.post<ExtractKeywordsResult>('/api/taxonomy/extract-keywords', {
    text,
    locale,
    limit,
  });
  if (res.error || !res.data) {
    return { skills: [], roles: [], unmatched: [] };
  }
  return res.data;
}

export async function scoreSkillOverlapApi(
  candidateSkills: string[],
  jobSkills: string[],
  locale?: TaxonomyLocale,
): Promise<ScoreSkillOverlapResult> {
  if (jobSkills.length === 0) {
    return { score: 0, matched: [], gaps: [] };
  }

  const res = await api.post<ScoreSkillOverlapResult>('/api/taxonomy/score-skill-overlap', {
    candidateSkills,
    jobSkills,
    locale,
  });
  if (res.error || !res.data) {
    return { score: 0, matched: [], gaps: [] };
  }
  return res.data;
}
